import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { composeAddress, makeApi, type AdminCustomerSummary, type FlagReason } from '../api/client';

const FLAG_OPTIONS: { value: '' | FlagReason; label: string }[] = [
  { value: '', label: 'All flag reasons' },
  { value: 'duplicate_address_claim', label: 'Duplicate address claim' },
  { value: 'no_project_mapping', label: 'No project mapping' },
  { value: 'invalid_address_id', label: 'Invalid address id' },
  { value: 'pending_too_long', label: 'Pending too long' },
  { value: 'high_issue_volume', label: 'High issue volume' },
];

export function AdminCustomersPage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [customers, setCustomers] = useState<AdminCustomerSummary[]>([]);
  const [accountStatus, setAccountStatus] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      const res = await api.adminListCustomers({
        accountStatus: accountStatus || undefined,
        flagReason: flagReason || undefined,
        search: search || undefined,
      });
      setCustomers(res.customers);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [accountStatus, flagReason]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="label">Status</label>
          <select
            className="input"
            value={accountStatus}
            onChange={(e) => setAccountStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="approved">Approved</option>
            <option value="pending_review">Pending review</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div>
          <label className="label">Flag</label>
          <select
            className="input"
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
          >
            {FLAG_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:flex-1 sm:min-w-[200px]">
          <label className="label">Search email</label>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Press enter to search"
          />
        </div>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}
      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <div className="card p-0">
          {/* Mobile card list */}
          <ul className="sm:hidden divide-y divide-gray-800">
            {customers.map((c) => (
              <li key={c.sub} className="p-4 hover:bg-gray-800/40 transition-colors">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/admin/customers/${c.sub}`}
                      className="text-red-400 hover:text-red-300 hover:underline text-sm font-medium truncate block"
                    >
                      {c.email}
                    </Link>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="badge bg-gray-800 text-gray-300 border border-gray-700">
                      {c.accountStatus}
                    </span>
                    {c.flagged && (
                      <span className="badge bg-red-900/60 text-red-300 border border-red-700/50">
                        flagged
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                  <span className="text-gray-400">
                    {c.accountTier === 'advanced'
                      ? 'Multiple addresses'
                      : c.addresses && c.addresses.length > 0
                      ? composeAddress(c.addresses[0])
                      : '—'}
                  </span>
                  <span>{new Date(c.createdAt).toLocaleDateString()}</span>
                </div>
                {c.flagReasons.length > 0 && (
                  <div className="mt-1 text-xs text-gray-500">{c.flagReasons.join(', ')}</div>
                )}
              </li>
            ))}
            {customers.length === 0 && (
              <li className="p-4 text-center text-gray-500 text-sm">No customers</li>
            )}
          </ul>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/60 text-left text-gray-400 border-b border-gray-700">
              <tr>
                <th className="p-3">Email</th>
                <th className="p-3">Status</th>
                <th className="p-3">Address</th>
                <th className="p-3">Flagged</th>
                <th className="p-3">Reasons</th>
                <th className="p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.sub} className="border-t border-gray-800 hover:bg-gray-800/40 transition-colors">
                  <td className="p-3">
                    <Link
                      to={`/admin/customers/${c.sub}`}
                      className="text-red-400 hover:text-red-300 hover:underline"
                    >
                      {c.email}
                    </Link>
                  </td>
                  <td className="p-3">
                    <span className="badge bg-gray-800 text-gray-300 border border-gray-700">{c.accountStatus}</span>
                  </td>
                  <td className="p-3 text-gray-300">
                    {c.accountTier === 'advanced' ? (
                      <span className="text-gray-500">Multiple addresses</span>
                    ) : c.addresses && c.addresses.length > 0 ? (
                      composeAddress(c.addresses[0])
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {c.flagged ? (
                      <span className="badge bg-red-900/60 text-red-300 border border-red-700/50">flagged</span>
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-gray-400">{c.flagReasons.join(', ') || '—'}</td>
                  <td className="p-3 text-xs text-gray-500">
                    {new Date(c.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
              {customers.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-500">
                    No customers
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
